
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  error: Error | null;
  signOut: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  error: null,
  signOut: async () => {},
  isAdmin: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user || null);
        
        // Check for email confirmation event and redirect to success page
        if (event === 'SIGNED_IN') {
          const isJustConfirmed = localStorage.getItem('emailJustConfirmed');
          if (isJustConfirmed === 'true') {
            localStorage.removeItem('emailJustConfirmed'); // Clear the flag
            window.location.href = '/email-confirmation-success';
          }
        }
        
        // Update admin status when auth state changes - using secure database function
        if (session?.user) {
          // Defer the role check to avoid blocking auth state changes
          setTimeout(() => {
            checkAdminRole(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
        }
        
        setLoading(false);
      }
    );

    // Check URL for confirmation indications
    const checkForConfirmation = () => {
      const url = new URL(window.location.href);
      // This captures Supabase's email confirmation redirect
      if (url.hash.includes('access_token') && url.hash.includes('type=recovery') || 
          url.hash.includes('type=signup')) {
        localStorage.setItem('emailJustConfirmed', 'true');
      }
    };
    
    checkForConfirmation();

    // THEN check for existing session
    const getInitialSession = async () => {
      try {
        setLoading(true);
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        setSession(session);
        setUser(session?.user || null);
        
        // Check if user has admin role using secure database function
        if (session?.user) {
          await checkAdminRole(session.user.id);
        }
      } catch (error) {
        setError(error as Error);
        console.error("Error getting initial session:", error);
      } finally {
        setLoading(false);
      }
    };

    // Secure function to check admin role from database
    const checkAdminRole = async (userId: string) => {
      try {
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: userId,
          _role: 'admin'
        });
        
        if (error) {
          console.error("Error checking admin role:", error);
          setIsAdmin(false);
        } else {
          setIsAdmin(data || false);
        }
      } catch (error) {
        console.error("Error checking admin role:", error);
        setIsAdmin(false);
      }
    };

    getInitialSession();

    // Clean up the listener when the component unmounts
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      // Let the onAuthStateChange handle the state update
      // Don't navigate here - the router will handle navigation based on auth state
    } catch (error) {
      console.error("Error signing out:", error);
      setError(error as Error);
    }
  };

  const value = {
    session,
    user,
    loading,
    error,
    signOut,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
